import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import type { Service } from '../graphqlEnums'
import { getComposerStore } from '../store/composerStore'
import { selectEditorForDraft } from '../store/editorSlice'
import type { ChannelVariant } from '../store/postSlice/types'
import { channelVariantToEditPostInput } from './channelVariantToEditPostInput'
import {
  checkEditPostPayloadDrift,
  reportEditPostPayloadBuildFailure,
} from './editPostPayloadDrift'
import type { EditPostInput } from './graphqlTypes'
import { mapToEditPostInput } from './mapToEditPostInput'
import { resolveService } from './mapSharedPostFields'
import { serviceRegistry } from './metadata'
import type { MapperContext } from './types'

/** Builds the Zustand-sourced payload and runs the drift check against it. */
function buildAndCheckVariantInput(
  draft: Draft,
  ctx: MapperContext,
  postId: string,
  service: Service,
  channelVariant: ChannelVariant,
): EditPostInput {
  const metadata = serviceRegistry[service].mapper(draft, {
    ...ctx,
    channelVariant,
  })
  const newInput = channelVariantToEditPostInput(
    channelVariant,
    service,
    metadata,
    ctx,
    postId,
  )
  checkEditPostPayloadDrift(
    draft,
    ctx,
    ctx.channelId,
    service,
    postId,
    newInput,
  )
  return newInput
}

/**
 * Maps the draft being edited to an `EditPostInput`. A mirrored
 * `channelVariant` always builds the Zustand-sourced payload and runs
 * `checkEditPostPayloadDrift` against it, independent of `useZustandPayload`
 * — so drift signal keeps accumulating regardless of the
 * `composer-zustand-edit-post` rollout. `useZustandPayload` only decides
 * which payload ships; absence of a variant (not yet mirrored) always falls
 * back to the legacy `mapToEditPostInput`. When the flag is off, the
 * comparison build is a side quest — a bug in the still-unramped mapper must
 * not break a save nothing here depends on, so failures there are reported
 * rather than thrown.
 */
export function buildEditPostInput(
  draft: Draft,
  ctx: MapperContext,
  postId: string,
  channelVariant: ChannelVariant | undefined,
  useZustandPayload: boolean,
  editorStateEnabled: boolean,
): EditPostInput {
  const ctxWithEditor: MapperContext = {
    ...ctx,
    editor: editorStateEnabled
      ? selectEditorForDraft(getComposerStore().getState(), draft.id)
      : undefined,
  }

  if (!channelVariant) {
    return mapToEditPostInput(draft, ctxWithEditor, postId)
  }

  const service = resolveService(draft)

  if (useZustandPayload) {
    return buildAndCheckVariantInput(
      draft,
      ctxWithEditor,
      postId,
      service,
      channelVariant,
    )
  }

  try {
    buildAndCheckVariantInput(
      draft,
      ctxWithEditor,
      postId,
      service,
      channelVariant,
    )
  } catch (error) {
    reportEditPostPayloadBuildFailure(ctx.channelId, service, error)
  }
  return mapToEditPostInput(draft, ctxWithEditor, postId)
}
